import React from 'react';
import { Box, Flex, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  Buildings,
  CaretDown,
  CaretUp,
  CheckCircle,
  FileText,
  ListChecks,
  UploadSimple,
  UserFocus,
} from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import type {
  IContaCilOperacional,
  IContaCilOperKpi,
} from '../../../../../hooks/useContaCilDashboard';
import { InfoTip } from './InfoTip';
import { Sparkline, computeDelta, deltaColorFor } from './KPICards';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface OperCardDef {
  key: keyof IContaCilOperacional;
  label: string;
  icon: React.ReactNode;
  accent: string;
  // Queda é boa (pendências/retenções)? Alta é boa (exportado)?
  positiveWhenDown?: boolean;
  // Número grande do card.
  display: (k: IContaCilOperKpi) => string;
  // Sub-linha de contexto.
  sub: (k: IContaCilOperKpi) => string;
  // Texto do tooltip explicativo (o que o número significa).
  info: string;
}

const CARDS: OperCardDef[] = [
  {
    key: 'pendentes',
    label: 'Pendentes conciliação',
    icon: <ListChecks size={18} weight="duotone" />,
    accent: '#F59E0B',
    positiveWhenDown: true,
    display: k => formatNumber(k.valor),
    sub: k => `de ${formatNumber(k.contexto ?? 0)} importados`,
    info: 'Lançamentos ainda não conciliados no período (não vinculados ao plano de contas).',
  },
  {
    key: 'aguardando_exportacao',
    label: 'Aguardando exportação',
    icon: <UploadSimple size={18} weight="duotone" />,
    accent: '#E08A06',
    positiveWhenDown: true,
    display: k => formatNumber(k.valor),
    sub: k => `${formatNumber(k.contexto ?? 0)} já exportados`,
    info: 'Já conciliados e elegíveis (partida-dobrada), mas ainda não exportados para o Domínio.',
  },
  {
    key: 'empresas_fora_operacao',
    label: 'Empresas fora de operação',
    icon: <Buildings size={18} weight="duotone" />,
    accent: '#EF4444',
    positiveWhenDown: true,
    display: k => formatNumber(k.valor),
    sub: k => `${formatNumber(k.contexto ?? 0)} em operação`,
    info: 'Empresas que usam o ContaCil mas não enviaram nenhum arquivo no período selecionado.',
  },
  {
    key: 'extratos_nao_homologados',
    label: 'Extratos não homologados',
    icon: <FileText size={18} weight="duotone" />,
    accent: '#1B88F5',
    positiveWhenDown: true,
    display: k => formatNumber(k.valor),
    sub: k => `de ${formatNumber(k.contexto ?? 0)} extraídos (PDF)`,
    info: 'Extratos em PDF importados que ainda não passaram pela extração/validação (homologação). Só PDF exige isso.',
  },
  {
    key: 'aguardando_identificacao',
    label: 'Aguardando identificação',
    icon: <UserFocus size={18} weight="duotone" />,
    accent: '#8B5CF6',
    positiveWhenDown: true,
    display: k => formatNumber(k.valor),
    sub: () => 'sem empresa detectada',
    info: 'Importações cuja empresa não foi detectada pelo CNPJ — precisam de vínculo manual pra seguir.',
  },
  {
    key: 'exportado_pct',
    label: 'Exportado · Domínio',
    icon: <CheckCircle size={18} weight="duotone" />,
    accent: '#10B981',
    // valor = exportados; contexto = elegíveis → % = valor/contexto.
    display: k => {
      const pct = k.contexto && k.contexto > 0 ? (k.valor / k.contexto) * 100 : 0;
      return `${pct.toFixed(1).replace('.', ',')}%`;
    },
    sub: k => `${formatNumber(k.valor)} de ${formatNumber(k.contexto ?? 0)}`,
    info: '% dos lançamentos elegíveis (partida-dobrada conciliada) já exportados para o Domínio.',
  },
];

interface OperacionalKpisProps {
  data?: IContaCilOperacional;
  isLoading?: boolean;
}

/** Os 6 KPIs operacionais do hub — barra de acento + valor + sparkline + variação. */
export const OperacionalKpis = ({ data, isLoading }: OperacionalKpisProps) => {
  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={8}>
      {CARDS.map((card, idx) => {
        const kpi = data?.[card.key];
        const serie = kpi?.serie ?? [];
        const delta = serie.length ? computeDelta(serie) : null;
        const deltaColor = deltaColorFor(delta, card.positiveWhenDown);
        return (
          <motion.div
            key={card.key}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 * idx, ease: [0.4, 0, 0.2, 1] }}
          >
            <GlassCard intensity="strong" p={0} h="full" overflow="hidden">
              <Box h="3px" w="full" bg={card.accent} />
              <Flex direction="column" gap={2} h="full" p={4}>
                <Flex align="center" gap={1.5}>
                  <Box color={card.accent} display="flex">
                    {card.icon}
                  </Box>
                  <Text
                    fontSize="9.5px"
                    fontWeight="600"
                    color="#94A3B8"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    noOfLines={2}
                    flex={1}
                  >
                    {card.label}
                  </Text>
                  <InfoTip label={card.info} size={12} />
                </Flex>

                <Flex align="flex-end" justify="space-between" gap={1}>
                  {isLoading || !kpi ? (
                    <Skeleton height="26px" width="60%" mt={1} borderRadius="md" />
                  ) : (
                    <Text
                      fontSize={{ base: '22px', md: '26px' }}
                      fontWeight="700"
                      color="#0F172A"
                      letterSpacing="-0.02em"
                      lineHeight="1.05"
                    >
                      {card.display(kpi)}
                    </Text>
                  )}
                  {!isLoading && serie.length > 1 && (
                    <Box flexShrink={0} pb={1}>
                      <Sparkline data={serie} color={card.accent} />
                    </Box>
                  )}
                </Flex>

                {!isLoading && kpi && (
                  <Flex align="center" gap={2} mt="auto" wrap="wrap">
                    {delta != null && delta !== 0 && (
                      <Flex
                        align="center"
                        gap={0.5}
                        px={1.5}
                        py={0.5}
                        borderRadius="md"
                        bg={`${deltaColor}14`}
                        color={deltaColor}
                      >
                        {delta < 0 ? (
                          <CaretDown size={10} weight="bold" />
                        ) : (
                          <CaretUp size={10} weight="bold" />
                        )}
                        <Text fontSize="10px" fontWeight="700">
                          {formatNumber(Math.abs(delta))}
                        </Text>
                      </Flex>
                    )}
                    <Text fontSize="10.5px" color="#94A3B8" fontWeight="500" noOfLines={1}>
                      {card.sub(kpi)}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </GlassCard>
          </motion.div>
        );
      })}
    </SimpleGrid>
  );
};
